import {
  useEffect,
  useId,
  useMemo,
  useRef,
  useState,
  type KeyboardEvent,
} from 'react';
import { BookOpen, Search, X } from 'lucide-react';
import { cn } from '@ai-tutor/ui';
import type { FocusLessonOption } from './focusViewModel';

interface LessonPickerProps {
  lessons: FocusLessonOption[];
  /** The linked lesson, if any. */
  selectedId?: string;
  onPick: (lesson: FocusLessonOption) => void;
  onUnlink: () => void;
  /** A search that matched nothing becomes the session's name. */
  onUseText: (text: string) => void;
  /** Stories open the list to show it at rest. */
  defaultOpen?: boolean;
}

const RECENT = 5;

/**
 * Link the session to a lesson: the five most recent first, every lesson a
 * search away. The list scrolls inside its popover, never the page; long
 * titles truncate and show in full on hover. No lessons (a new account, or
 * the request failed): nothing renders, and free text is all there is.
 */
export function LessonPicker({
  lessons,
  selectedId,
  onPick,
  onUnlink,
  onUseText,
  defaultOpen = false,
}: LessonPickerProps) {
  const [open, setOpen] = useState(defaultOpen);
  const [query, setQuery] = useState('');
  const [active, setActive] = useState(0);
  const root = useRef<HTMLDivElement>(null);
  const trigger = useRef<HTMLButtonElement>(null);
  const listId = useId();
  const selected = lessons.find(l => l.id === selectedId);

  const matches = useMemo(() => {
    const q = query.trim().toLowerCase();
    return q ? lessons.filter(l => l.title.toLowerCase().includes(q)) : lessons;
  }, [lessons, query]);

  useEffect(() => {
    if (!open) return;
    const onDown = (e: MouseEvent) => {
      if (!root.current?.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener('mousedown', onDown);
    return () => document.removeEventListener('mousedown', onDown);
  }, [open]);

  if (!lessons.length) return null;

  if (selected) {
    return (
      <span
        className='inline-flex h-8 max-w-full items-center gap-1.5 rounded-full bg-secondary pl-3 pr-1 text-xs font-medium text-foreground'
        title={selected.title}
      >
        <BookOpen className='h-3.5 w-3.5 shrink-0' aria-hidden='true' />
        <span className='truncate'>{selected.title}</span>
        <button
          type='button'
          onClick={onUnlink}
          aria-label={`Unlink ${selected.title}`}
          className='tap-target flex h-6 w-6 shrink-0 items-center justify-center rounded-full text-muted-foreground hover:bg-card hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring'
        >
          <X className='h-3.5 w-3.5' aria-hidden='true' />
        </button>
      </span>
    );
  }

  const close = () => {
    setOpen(false);
    trigger.current?.focus();
  };
  const pick = (lesson: FocusLessonOption) => {
    onPick(lesson);
    setOpen(false);
  };
  const onKey = (e: KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Escape') {
      e.stopPropagation();
      close();
    } else if (e.key === 'ArrowDown' || e.key === 'ArrowUp') {
      e.preventDefault();
      setActive(a =>
        Math.max(
          0,
          Math.min(matches.length - 1, a + (e.key === 'ArrowDown' ? 1 : -1))
        )
      );
    } else if (e.key === 'Enter') {
      e.preventDefault();
      if (matches[active]) pick(matches[active]);
    }
  };
  const groups: [string, FocusLessonOption[]][] = query.trim()
    ? [['Matches', matches]]
    : [
        ['Recent', matches.slice(0, RECENT)],
        ['All lessons', matches.slice(RECENT)],
      ];
  let index = -1;

  return (
    <div ref={root} className='relative'>
      <button
        ref={trigger}
        type='button'
        aria-haspopup='listbox'
        aria-expanded={open}
        onClick={() => {
          setOpen(o => !o);
          setQuery('');
          setActive(0);
        }}
        className='inline-flex h-8 items-center gap-1.5 rounded-lg border border-dashed border-input px-2.5 text-xs font-medium text-muted-foreground transition-colors duration-fast hover:border-solid hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring max-sm:h-11'
      >
        <BookOpen className='h-3.5 w-3.5' aria-hidden='true' />
        Link a lesson
        <span className='font-normal'>(optional)</span>
      </button>
      {open && (
        <div className='absolute inset-x-0 top-10 z-30 flex flex-col gap-1.5 rounded-xl border border-border bg-popover p-2 text-popover-foreground shadow-elevation-2'>
          <label className='relative block'>
            <span className='sr-only'>Search lessons</span>
            <Search
              className='pointer-events-none absolute left-2.5 top-2.5 h-4 w-4 text-muted-foreground'
              aria-hidden='true'
            />
            <input
              // eslint-disable-next-line jsx-a11y/no-autofocus -- the popover opens to type in
              autoFocus
              role='combobox'
              aria-expanded='true'
              aria-controls={listId}
              aria-activedescendant={
                matches[active] ? `${listId}-${matches[active].id}` : undefined
              }
              value={query}
              onChange={e => {
                setQuery(e.target.value);
                setActive(0);
              }}
              onKeyDown={onKey}
              placeholder={`Search ${lessons.length} lessons`}
              className='h-9 w-full rounded-md border border-input bg-background pl-8 pr-3 text-sm text-foreground placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring max-sm:h-11 max-sm:text-base'
            />
          </label>
          <div
            id={listId}
            role='listbox'
            aria-label='Lessons'
            className='max-h-56 overflow-y-auto overscroll-contain'
          >
            {matches.length === 0 ? (
              <div className='flex flex-col items-start gap-2 px-2 py-3 text-sm text-muted-foreground'>
                No lesson matches “{query}”.
                <button
                  type='button'
                  onClick={() => {
                    onUseText(query.trim());
                    setOpen(false);
                  }}
                  className='rounded-md border border-input px-2.5 py-1 text-xs font-medium text-foreground hover:bg-secondary'
                >
                  Use it as the session’s name
                </button>
              </div>
            ) : (
              groups.map(([name, items]) =>
                items.length ? (
                  <div key={name} role='group' aria-label={name}>
                    <p className='px-2 pb-1 pt-2 font-mono text-xs uppercase tracking-wider text-muted-foreground'>
                      {name}
                    </p>
                    {items.map(lesson => {
                      index++;
                      const i = index;
                      return (
                        <div
                          key={lesson.id}
                          id={`${listId}-${lesson.id}`}
                          role='option'
                          aria-selected={i === active}
                          title={lesson.title}
                          tabIndex={-1}
                          onMouseEnter={() => setActive(i)}
                          onClick={() => pick(lesson)}
                          onKeyDown={e => e.key === 'Enter' && pick(lesson)}
                          className={cn(
                            'flex cursor-pointer items-center gap-2 rounded-md px-2 py-2 text-sm text-foreground',
                            i === active && 'bg-secondary'
                          )}
                        >
                          <BookOpen
                            className='h-3.5 w-3.5 shrink-0 text-muted-foreground'
                            aria-hidden='true'
                          />
                          <span className='truncate'>{lesson.title}</span>
                        </div>
                      );
                    })}
                  </div>
                ) : null
              )
            )}
          </div>
        </div>
      )}
    </div>
  );
}
